/**
 * Racing Renderer - Pseudo-3D Road Engine
 *
 * Classic OutRun-style rendering: project road segments from world space
 * to screen space, draw near-to-far with hill clipping.
 */

// Bright retro color palette (Super Monaco GP inspired)
const COLORS = {
  SKY_TOP: '#3399dd',
  SKY_BOTTOM: '#66ccee',
  HORIZON: '#99eeff',
  GRASS_LIGHT: '#10aa10',
  GRASS_DARK: '#009a00',
  ROAD_LIGHT: '#6b6b6b',
  ROAD_DARK: '#636363',
  KERB_RED: '#cc0000',
  KERB_WHITE: '#ffffff',
  LANE_WHITE: '#cccccc',
  RUMBLE_LIGHT: '#555555',
  RUMBLE_DARK: '#bbbbbb',
};

const CAMERA_HEIGHT = 1000;
const ROAD_WIDTH = 2000;
const FOV = 100; // degrees

// Persistent background scroll offset (driven by road curves, not forward movement)
let bgCurveOffset = 0;

// Smoothed camera X to prevent jitter
let smoothCameraX = 0;

// Countdown animation state
let lastCountdownValue = -1;
let countdownChangeTime = 0;

// Car sprite management
const CAR_SPRITES = {
  hardLeft: null,
  left: null,
  straight: null,
  right: null,
  hardRight: null,
  loaded: false,
};

export function loadCarSprites() {
  const paths = {
    hardLeft: '/assets/img/raceing/car-behind-hard-left.png',
    left: '/assets/img/raceing/car-behind-left.png',
    straight: '/assets/img/raceing/car-behind.png',
    right: '/assets/img/raceing/car-behind-right.png',
    hardRight: '/assets/img/raceing/car-behind-hard-right.png',
  };

  const promises = Object.entries(paths).map(([key, src]) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => { CAR_SPRITES[key] = img; resolve(); };
      img.onerror = () => resolve();
      img.src = src;
    });
  });

  return Promise.all(promises).then(() => { CAR_SPRITES.loaded = true; });
}

// Countdown light sprites
const LIGHT_SPRITES = { 0: null, 1: null, 2: null, go: null, loaded: false };

// Background images (pixel art parallax layers)
const BG_IMAGES = { far: null, mid: null, near: null, loaded: false };

function loadLightSprites() {
  const paths = {
    0: '/assets/img/raceing/lights-0.png',
    1: '/assets/img/raceing/lights-1.png',
    2: '/assets/img/raceing/lights-2.png',
    go: '/assets/img/raceing/lights-go.png',
  };
  const promises = Object.entries(paths).map(([key, src]) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => { LIGHT_SPRITES[key] = img; resolve(); };
      img.onerror = () => resolve();
      img.src = src;
    });
  });
  return Promise.all(promises).then(() => { LIGHT_SPRITES.loaded = true; });
}

function loadBackgroundImages() {
  const paths = {
    far: '/assets/img/raceing/bg-far.png',
    mid: '/assets/img/raceing/bg-mid.png',
    near: '/assets/img/raceing/bg-near.png',
  };
  const promises = Object.entries(paths).map(([key, src]) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => { BG_IMAGES[key] = img; resolve(); };
      img.onerror = () => resolve();
      img.src = src;
    });
  });
  return Promise.all(promises).then(() => { BG_IMAGES.loaded = true; });
}

// Roadside sprite images
const ROADSIDE_SPRITES = {};
const SPRITE_CONFIGS = {
  pine:    { src: '/assets/img/raceing/sprite-pine.png', scale: 400 },
  pine2:   { src: '/assets/img/raceing/sprite-pine2.png', scale: 400 },
  palm:    { src: '/assets/img/raceing/sprite-palm.png', scale: 360 },
  sequoia: { src: '/assets/img/raceing/sprite-sequoia.png', scale: 440 },
  pole:    { src: '/assets/img/raceing/sprite-pole.png', scale: 350 },
  barrier: { src: '/assets/img/raceing/sprite-barrier.png', scale: 140 },
  cow:     { src: '/assets/img/raceing/sprite-cow.png', scale: 220 },
  cow2:    { src: '/assets/img/raceing/sprite-cow2.png', scale: 220 },
  deer:    { src: '/assets/img/raceing/sprite-deer.png', scale: 230 },
  gantryAd:    { src: '/assets/img/raceing/sprite-gantry-advert.png', scale: 350 },
  gantryBlank: { src: '/assets/img/raceing/sprite-gantry-blank.png', scale: 350 },
};

export function loadRoadsideSprites() {
  const promises = Object.entries(SPRITE_CONFIGS).map(([key, cfg]) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => { ROADSIDE_SPRITES[key] = img; resolve(); };
      img.onerror = () => resolve();
      img.src = cfg.src;
    });
  });
  // Also load background images and countdown lights
  promises.push(loadBackgroundImages());
  promises.push(loadLightSprites());
  return Promise.all(promises);
}

/**
 * Draw a filled trapezoid (used for road/kerb/grass polygons).
 */
function drawTrapezoid(ctx, x1, y1, w1, x2, y2, w2, color) {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x1 - w1, y1);
  ctx.lineTo(x1 + w1, y1);
  ctx.lineTo(x2 + w2, y2);
  ctx.lineTo(x2 - w2, y2);
  ctx.closePath();
  ctx.fill();
}

/**
 * Draw a sprite (image-based with procedural fallback).
 */
function drawSprite(ctx, type, screenX, screenY, scale, billboardText, segIndex, tint) {
  if (scale <= 0.001) return;
  const hasTint = tint !== undefined && tint < 1;
  if (hasTint) ctx.globalAlpha = tint;

  // Try image-based sprites
  let img = null;
  let spriteScale = 120;

  if (type === 'tree') {
    const treeTypes = ['pine', 'pine2', 'sequoia', 'pine', 'palm', 'pine2'];
    const key = treeTypes[(segIndex || 0) % treeTypes.length];
    img = ROADSIDE_SPRITES[key];
    spriteScale = (SPRITE_CONFIGS[key] || {}).scale || 150;
  } else if (type === 'barrier') {
    img = ROADSIDE_SPRITES.barrier;
    spriteScale = SPRITE_CONFIGS.barrier.scale;
  } else if (type === 'cow') {
    img = ROADSIDE_SPRITES[(segIndex || 0) % 2 === 0 ? 'cow' : 'cow2'];
    spriteScale = 220;
  } else if (type === 'deer') {
    img = ROADSIDE_SPRITES.deer;
    spriteScale = 230;
  } else if (type === 'pole') {
    img = ROADSIDE_SPRITES.pole;
    spriteScale = SPRITE_CONFIGS.pole.scale;
  }

  if (img) {
    const s = scale * spriteScale;
    if (s < 3) return;
    const aspect = img.height / img.width;
    const drawW = s;
    const drawH = s * aspect;
    ctx.drawImage(img, screenX - drawW / 2, screenY - drawH, drawW, drawH);
    if (hasTint) ctx.globalAlpha = 1;
    return;
  }

  // No procedural fallback — only image sprites used
  if (hasTint) ctx.globalAlpha = 1;
}

/**
 * Draw overhead gantry sign using image sprite, spanning the road.
 */
const GANTRY_QUOTES = ['FLOOR IT!', 'SEND IT!', 'STAY CURIOUS!', 'NO BRAKES!', 'FULL BEANS!', 'KEEP GOING!'];

function drawGantrySign(ctx, roadCenterX, roadY, scale, screenW, gantryIdx) {
  const isAd = gantryIdx % 2 === 0;
  const img = ROADSIDE_SPRITES[isAd ? 'gantryAd' : 'gantryBlank'];
  if (!img) return;
  const s = scale * 350;
  if (s < 3) return;
  const aspect = img.height / img.width;
  // Width spans the road (scale with road width)
  const drawW = screenW * 2.4;
  const drawH = drawW * aspect;
  ctx.drawImage(img, roadCenterX - drawW / 2, roadY - drawH, drawW, drawH);

  // Draw motivational quote on blank banners
  if (!isAd && s > 10) {
    const txt = GANTRY_QUOTES[Math.floor(gantryIdx / 2) % GANTRY_QUOTES.length];
    const fontSize = Math.max(6, Math.floor(drawW * 0.06));
    ctx.save();
    ctx.font = 'bold ' + fontSize + 'px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ffffff';
    // Text on the pink bar at the top of the banner
    ctx.fillText(txt, roadCenterX, roadY - drawH * 0.88);
    ctx.restore();
  }
}

function drawTree(ctx, scale) {
  const s = scale * 400;
  if (s < 2) return;

  // Trunk
  ctx.fillStyle = '#5a3010';
  ctx.fillRect(-s * 0.06, -s * 0.5, s * 0.12, s * 0.5);

  // Canopy layers (chunky pixel look)
  ctx.fillStyle = '#006400';
  ctx.beginPath();
  ctx.arc(0, -s * 0.55, s * 0.28, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = '#008800';
  ctx.beginPath();
  ctx.arc(-s * 0.08, -s * 0.65, s * 0.18, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = '#00aa00';
  ctx.beginPath();
  ctx.arc(s * 0.06, -s * 0.72, s * 0.12, 0, Math.PI * 2);
  ctx.fill();
}

function drawGrandstand(ctx, scale) {
  const s = scale * 350;
  if (s < 3) return;
  const w = s * 1.0;
  const h = s * 0.6;

  // Structure
  ctx.fillStyle = '#777777';
  ctx.fillRect(-w / 2, -h, w, h);

  // Roof
  ctx.fillStyle = '#cc0000';
  ctx.fillRect(-w / 2 - s * 0.05, -h - s * 0.1, w + s * 0.1, s * 0.1);

  // Crowd - rows of colored pixels
  const dotS = Math.max(2, s * 0.06);
  const cols = Math.floor(w / (dotS * 1.8));
  const crowdColors = ['#ff4444', '#4444ff', '#ffff44', '#44ff44', '#ff44ff', '#ffffff', '#ff8800', '#44ffff'];
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < cols; col++) {
      ctx.fillStyle = crowdColors[(row * cols + col) % crowdColors.length];
      const cx = -w / 2 + dotS + col * dotS * 1.8;
      const cy = -h + s * 0.15 + row * dotS * 2;
      ctx.fillRect(cx, cy, dotS, dotS);
    }
  }
}

function drawBillboard(ctx, scale, text) {
  const s = scale * 350;
  if (s < 3) return;
  const w = s * 0.9;
  const h = s * 0.35;
  const postH = s * 0.55;

  // Posts
  ctx.fillStyle = '#555555';
  ctx.fillRect(-w / 2 + s * 0.06, -postH, s * 0.04, postH);
  ctx.fillRect(w / 2 - s * 0.1, -postH, s * 0.04, postH);

  // Board background
  ctx.fillStyle = '#1a1a2e';
  ctx.fillRect(-w / 2, -postH - h, w, h);

  // Red border
  ctx.strokeStyle = '#cc0000';
  ctx.lineWidth = Math.max(1, s * 0.025);
  ctx.strokeRect(-w / 2, -postH - h, w, h);

  // Text
  if (text && s > 8) {
    const fontSize = Math.max(6, Math.floor(s * 0.16));
    ctx.font = `bold ${fontSize}px monospace`;
    ctx.fillStyle = '#ffcc00';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 0, -postH - h / 2);
    ctx.textAlign = 'start';
    ctx.textBaseline = 'alphabetic';
  }
}

function drawBarrier(ctx, scale) {
  const s = scale * 120;
  if (s < 2) return;
  const w = s * 0.3;
  const h = s * 0.15;

  for (let i = 0; i < 4; i++) {
    ctx.fillStyle = i % 2 === 0 ? '#cc0000' : '#ffffff';
    ctx.fillRect(-w / 2 + i * (w / 4), -h, w / 4, h);
  }
}

/**
 * Draw the player car using sprite images (falls back to procedural if not loaded).
 */
function drawPlayerCar(ctx, canvasW, canvasH, steerInput, speed, maxSpeed) {
  // Select sprite based on steering input
  let sprite = CAR_SPRITES.straight;
  if (steerInput < -0.6) sprite = CAR_SPRITES.hardLeft;
  else if (steerInput < -0.2) sprite = CAR_SPRITES.left;
  else if (steerInput > 0.6) sprite = CAR_SPRITES.hardRight;
  else if (steerInput > 0.2) sprite = CAR_SPRITES.right;

  if (sprite && CAR_SPRITES.loaded) {
    const carW = Math.min(200, canvasW * 0.22);
    const aspect = sprite.height / sprite.width;
    const carH = carW * aspect;
    const cx = canvasW / 2;
    const cy = canvasH - carH / 2 - 10;

    ctx.save();
    ctx.imageSmoothingEnabled = false; // keep pixel art crisp

    ctx.drawImage(sprite, cx - carW / 2, cy - carH / 2, carW, carH);
    ctx.restore();
  } else {
    drawPlayerCarFallback(ctx, canvasW, canvasH, steerInput, speed, maxSpeed);
  }
}

/**
 * Procedural car drawing (fallback when sprites not loaded).
 */
function drawPlayerCarFallback(ctx, canvasW, canvasH, steerInput, speed, maxSpeed) {
  const carW = Math.min(140, canvasW * 0.16);
  const carH = carW * 0.55;
  const cx = canvasW / 2;
  const cy = canvasH - carH * 0.5 - 15;

  ctx.save();
  ctx.translate(cx, cy);

  const tilt = -steerInput * 0.04;
  ctx.rotate(tilt);

  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.beginPath();
  ctx.ellipse(0, carH * 0.35, carW * 0.55, carH * 0.12, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = '#111';
  ctx.fillRect(-carW * 0.54, -carH * 0.05, carW * 0.1, carH * 0.35);
  ctx.fillRect(carW * 0.44, -carH * 0.05, carW * 0.1, carH * 0.35);

  ctx.fillStyle = '#cc0000';
  ctx.fillRect(-carW * 0.38, -carH * 0.55, carW * 0.76, carH * 0.85);
  ctx.beginPath();
  ctx.moveTo(-carW * 0.38, -carH * 0.55);
  ctx.lineTo(-carW * 0.2, -carH * 0.75);
  ctx.lineTo(carW * 0.2, -carH * 0.75);
  ctx.lineTo(carW * 0.38, -carH * 0.55);
  ctx.fill();

  ctx.fillStyle = '#aa0000';
  ctx.fillRect(-carW * 0.5, -carH * 0.78, carW, carH * 0.06);

  ctx.fillStyle = '#222';
  ctx.fillRect(-carW * 0.15, -carH * 0.25, carW * 0.3, carH * 0.3);

  ctx.fillStyle = '#336699';
  ctx.beginPath();
  ctx.arc(0, -carH * 0.18, carW * 0.08, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = '#222';
  ctx.fillRect(-carW * 0.05, -carH * 0.45, carW * 0.1, carH * 0.2);
  ctx.fillRect(-carW * 0.42, carH * 0.22, carW * 0.84, carH * 0.06);
  ctx.fillRect(-carW * 0.12, carH * 0.12, carW * 0.04, carH * 0.12);
  ctx.fillRect(carW * 0.08, carH * 0.12, carW * 0.04, carH * 0.12);

  if (speed > 50) {
    const glow = Math.min(1, speed / maxSpeed);
    ctx.fillStyle = `rgba(255, ${Math.floor(120 * (1 - glow))}, 0, ${glow * 0.6})`;
    ctx.fillRect(-carW * 0.06, carH * 0.28, carW * 0.12, carH * 0.08);
  }

  ctx.fillStyle = '#fff';
  const numSize = Math.max(8, carW * 0.11);
  ctx.font = `bold ${Math.floor(numSize)}px monospace`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('1', 0, -carH * 0.02);
  ctx.textAlign = 'start';
  ctx.textBaseline = 'alphabetic';
  ctx.restore();
}

/**
 * Draw pixel art parallax background using loaded sprite sheet strips.
 * Scrolls horizontally based on road curves (not forward movement).
 * Each layer overlaps below the horizon so no sky gap is visible.
 */
function drawParallaxBackground(ctx, canvasW, horizonY, curveOffset, playerX) {
  if (!BG_IMAGES.loaded || !BG_IMAGES.far) {
    return; // sky gradient already drawn, nothing else needed until loaded
  }

  ctx.save();
  ctx.imageSmoothingEnabled = false; // crisp pixel art

  // Each layer: { image, parallaxRate, heightMult }
  // parallaxRate controls how much each layer shifts with curves
  // Far layers shift less (distant), near layers shift more
  // All layers extend well below horizonY to cover any gap with the grass
  const layers = [
    { img: BG_IMAGES.far,  rate: 0.15, heightMult: 2.0 },
    { img: BG_IMAGES.mid,  rate: 0.5,  heightMult: 1.4 },
    { img: BG_IMAGES.near, rate: 1.2,  heightMult: 1.1 },
  ];

  // Slight shift from player lateral position (looking around)
  const playerShift = (playerX || 0) * canvasW * 0.02;

  for (const layer of layers) {
    const img = layer.img;
    if (!img) continue;

    const aspect = img.width / img.height;
    // Scale layer height relative to canvas
    const drawH = Math.max(50, horizonY * 0.32 * layer.heightMult);
    const drawW = drawH * aspect;

    // Scroll offset from curve accumulation + player lateral shift
    const rawScroll = curveOffset * layer.rate * canvasW * 0.015 + playerShift * layer.rate * 0.3;
    const scroll = ((rawScroll % drawW) + drawW) % drawW;

    // Position: anchor so the bottom of the image sits well below the horizon
    // This ensures no sky gap between background and grass, even with hills
    const drawY = horizonY - drawH * 0.85;

    // Tile across canvas width (extra tiles on both sides for safety)
    const startX = -scroll;
    for (let x = startX - drawW; x < canvasW + drawW; x += drawW) {
      ctx.drawImage(img, x, drawY, drawW, drawH);
    }
  }

  ctx.restore();
}

/**
 * Main render function - draws one frame of the racing view.
 *
 * Uses the standard pseudo-3D approach:
 *  1. Project all visible segments
 *  2. Draw near-to-far (painter's algorithm - far segments overlay near)
 *  3. Hill clipping via maxY tracker
 */
export function renderScene(ctx, canvasW, canvasH, track, playerZ, playerX, speed, maxSpeed, isMobile, steerInput) {
  const { segments, segmentLength, totalSegments } = track;
  const DRAW_DISTANCE = isMobile ? 150 : 220;
  const cameraDepth = (canvasH / 2) / Math.tan((FOV / 2) * Math.PI / 180);

  ctx.clearRect(0, 0, canvasW, canvasH);

  // Sky gradient
  const skyGrad = ctx.createLinearGradient(0, 0, 0, canvasH * 0.5);
  skyGrad.addColorStop(0, COLORS.SKY_TOP);
  skyGrad.addColorStop(0.7, COLORS.SKY_BOTTOM);
  skyGrad.addColorStop(1, COLORS.HORIZON);
  ctx.fillStyle = skyGrad;
  ctx.fillRect(0, 0, canvasW, canvasH);

  // Base segment (ensure non-negative modulo)
  const baseSegIdx = ((Math.floor(playerZ / segmentLength) % totalSegments) + totalSegments) % totalSegments;
  const basePercent = (((playerZ % segmentLength) + segmentLength) % segmentLength) / segmentLength;

  // Pre-calculate cumulative hill heights
  const hillHeights = new Float32Array(DRAW_DISTANCE + 1);
  hillHeights[0] = 0;
  for (let n = 1; n <= DRAW_DISTANCE; n++) {
    const segIdx = (baseSegIdx + n) % totalSegments;
    const s = segments[segIdx];
    hillHeights[n] = hillHeights[n - 1] + (s ? s.hill * 30 : 0);
  }

  // Smooth camera X toward player position (prevents jitter on hard turns)
  smoothCameraX += (playerX - smoothCameraX) * 0.12;

  // Project all segments
  const projected = [];
  let cumulCurve = 0;
  let curveX = 0;

  for (let n = 0; n <= DRAW_DISTANCE; n++) {
    const segIdx = (baseSegIdx + n) % totalSegments;
    const seg = segments[segIdx];

    if (!seg) { projected.push(null); continue; }

    // Z distance from camera (in segment units)
    const dz = (n - basePercent) * segmentLength;
    if (dz <= 0) {
      projected.push(null);
      continue;
    }

    // Perspective scale
    const scale = cameraDepth / dz;

    // Project Y: camera is above road, road projects below center
    const roadHeight = hillHeights[n];
    const screenY = canvasH / 2 + (CAMERA_HEIGHT - roadHeight) * scale;
    const screenW = (ROAD_WIDTH / 2) * scale;

    // Curve accumulation (interpolate first segment for smooth transitions)
    const curveVal = n === 0 ? seg.curve * (1 - basePercent) : seg.curve;
    cumulCurve += curveVal;
    curveX += cumulCurve;

    const screenX = canvasW / 2 + curveX * scale * 10 - smoothCameraX * screenW * 3;

    projected.push({
      seg,
      screenX,
      screenY,
      screenW,
      scale,
    });
  }

  // Find approximate horizon Y for background
  const lastProj = projected[DRAW_DISTANCE];
  const horizonY = lastProj ? Math.min(lastProj.screenY, canvasH * 0.48) : canvasH * 0.45;

  // Accumulate background scroll from road curves (only shifts when turning)
  const currentSeg = segments[baseSegIdx];
  const currentCurve = currentSeg ? currentSeg.curve : 0;
  const speedRatio = speed / maxSpeed;
  bgCurveOffset += currentCurve * speedRatio * 0.15;

  // Draw parallax background (driven by curve offset + slight player steering shift)
  drawParallaxBackground(ctx, canvasW, horizonY, bgCurveOffset, smoothCameraX);

  // Fill everything below horizon with grass so parallax doesn't bleed through
  ctx.fillStyle = COLORS.GRASS_DARK;
  ctx.fillRect(0, horizonY, canvasW, canvasH - horizonY);

  // Draw segments near-to-far (standard pseudo-3D algorithm)
  // Each band: pNear (bottom, closer, larger Y) to pFar (top, farther, smaller Y)
  // clipY tracks the highest drawn point; segments behind hills get culled
  // Sprites are collected and drawn in a separate pass so grass doesn't cover them
  let clipY = canvasH;
  const spriteList = [];

  for (let n = 1; n <= DRAW_DISTANCE; n++) {
    const pNear = projected[n - 1]; // near/bottom edge (closer to camera)
    const pFar = projected[n];      // far/top edge (farther from camera)

    if (!pNear || !pFar) continue;

    // Back-face cull: far edge should be above near edge
    if (pFar.screenY >= pNear.screenY) continue;

    // Hill clip: skip if far edge is below the clip line
    if (pFar.screenY >= clipY) continue;

    const seg = pFar.seg;
    const isAlt = seg.color === 0;

    // Clamp the bottom of this band to the clip line
    const bandTop = pFar.screenY;
    const bandBot = Math.min(pNear.screenY, clipY);

    if (bandBot <= bandTop) continue;
    if (bandTop > canvasH) continue; // entirely off screen

    // Grass (full width band)
    ctx.fillStyle = isAlt ? COLORS.GRASS_LIGHT : COLORS.GRASS_DARK;
    ctx.fillRect(0, Math.max(0, bandTop), canvasW, Math.min(canvasH, bandBot) - Math.max(0, bandTop));

    // Rumble strips
    drawTrapezoid(ctx,
      pFar.screenX, pFar.screenY, pFar.screenW * 1.15,
      pNear.screenX, pNear.screenY, pNear.screenW * 1.15,
      isAlt ? COLORS.RUMBLE_LIGHT : COLORS.RUMBLE_DARK
    );

    // Kerbs
    drawTrapezoid(ctx,
      pFar.screenX, pFar.screenY, pFar.screenW * 1.08,
      pNear.screenX, pNear.screenY, pNear.screenW * 1.08,
      isAlt ? COLORS.KERB_RED : COLORS.KERB_WHITE
    );

    // Road surface
    drawTrapezoid(ctx,
      pFar.screenX, pFar.screenY, pFar.screenW,
      pNear.screenX, pNear.screenY, pNear.screenW,
      isAlt ? COLORS.ROAD_LIGHT : COLORS.ROAD_DARK
    );

    // Lane markings
    if (isAlt) {
      const lw1 = pFar.screenW * 0.015;
      const lw2 = pNear.screenW * 0.015;
      drawTrapezoid(ctx, pFar.screenX, pFar.screenY, lw1, pNear.screenX, pNear.screenY, lw2, COLORS.LANE_WHITE);

      const dw1 = pFar.screenW * 0.01;
      const dw2 = pNear.screenW * 0.01;
      drawTrapezoid(ctx,
        pFar.screenX - pFar.screenW * 0.5, pFar.screenY, dw1,
        pNear.screenX - pNear.screenW * 0.5, pNear.screenY, dw2,
        COLORS.LANE_WHITE
      );
      drawTrapezoid(ctx,
        pFar.screenX + pFar.screenW * 0.5, pFar.screenY, dw1,
        pNear.screenX + pNear.screenW * 0.5, pNear.screenY, dw2,
        COLORS.LANE_WHITE
      );
    }

    // Start/finish checkered flag drawn ON the road surface
    if (seg.index >= 0 && seg.index < 2) {
      const cols = 10;
      const rows = 2;
      const rowH = (pNear.screenY - pFar.screenY) / rows;
      for (let row = 0; row < rows; row++) {
        const rowTop = pFar.screenY + row * rowH;
        const rowBot = rowTop + rowH;
        // Interpolate road width at this row's Y position
        const t = (row + 0.5) / rows;
        const midX = pFar.screenX + (pNear.screenX - pFar.screenX) * t;
        const midW = pFar.screenW + (pNear.screenW - pFar.screenW) * t;
        const colW = (midW * 2) / cols;
        for (let c = 0; c < cols; c++) {
          ctx.fillStyle = ((c + row + seg.index) % 2 === 0) ? '#ffffff' : '#000000';
          ctx.fillRect(midX - midW + c * colW, rowTop, colW, rowH);
        }
      }
    }

    // Collect sprites for separate draw pass
    if (seg.sprite && pFar.screenY > 0 && pFar.screenY < canvasH) {
      const spriteOffset = seg.spriteSide * (pFar.screenW * 1.8);
      const spriteX = pFar.screenX + spriteOffset;

      if (spriteX > -200 && spriteX < canvasW + 200) {
        spriteList.push({
          type: seg.sprite, x: spriteX, y: pFar.screenY,
          scale: pFar.scale, text: seg.billboardText, idx: seg.index,
        });
      }
    }

    // Far tree layers — trees further from road on both sides
    if (seg.farTree && pFar.screenY > 0 && pFar.screenY < canvasH) {
      const farOffset = seg.farTreeSide * (pFar.screenW * 3.2);
      const farX = pFar.screenX + farOffset;
      if (farX > -300 && farX < canvasW + 300) {
        spriteList.push({
          type: 'tree', x: farX, y: pFar.screenY,
          scale: pFar.scale, idx: seg.index + 7, tint: 0.85,
        });
      }
    }
    if (seg.farTree2 && pFar.screenY > 0 && pFar.screenY < canvasH) {
      const farOffset = seg.farTree2Side * (pFar.screenW * 4.5);
      const farX = pFar.screenX + farOffset;
      if (farX > -300 && farX < canvasW + 300) {
        spriteList.push({
          type: 'tree', x: farX, y: pFar.screenY,
          scale: pFar.scale * 0.85, idx: seg.index + 13, tint: 0.65,
        });
      }
    }

    // Gantry signs — overhead signs spanning the road
    if (seg.gantrySign && pFar.screenY > 0 && pFar.screenY < canvasH) {
      spriteList.push({
        type: 'gantry', x: pFar.screenX, y: pFar.screenY,
        scale: pFar.scale, screenW: pFar.screenW, gantryIdx: seg.gantryIdx,
      });
    }

    // Update clip line: the far edge is the new highest drawn point
    clipY = pFar.screenY;
  }

  // Draw sprites far-to-near so closer sprites overlap farther ones
  ctx.imageSmoothingEnabled = false;
  for (let i = spriteList.length - 1; i >= 0; i--) {
    const sp = spriteList[i];
    if (sp.type === 'gantry') {
      drawGantrySign(ctx, sp.x, sp.y, sp.scale, sp.screenW, sp.gantryIdx);
    } else {
      drawSprite(ctx, sp.type, sp.x, sp.y, sp.scale, sp.text, sp.idx, sp.tint);
    }
  }

  ctx.imageSmoothingEnabled = true;

  // Player car
  drawPlayerCar(ctx, canvasW, canvasH, steerInput || 0, speed, maxSpeed);
}

// Blocky pixel font glyphs — 5 wide x 7 tall grid per character
// Each row is a bitmask (bit 4 = leftmost pixel, bit 0 = rightmost)
const BLOCK_FONT = {
  '0': [0b11111,0b10001,0b10001,0b10001,0b10001,0b10001,0b11111],
  '1': [0b00110,0b01110,0b00110,0b00110,0b00110,0b00110,0b01111],
  '2': [0b11111,0b00001,0b00001,0b11111,0b10000,0b10000,0b11111],
  '3': [0b11111,0b00001,0b00001,0b11111,0b00001,0b00001,0b11111],
  '4': [0b10001,0b10001,0b10001,0b11111,0b00001,0b00001,0b00001],
  '5': [0b11111,0b10000,0b10000,0b11111,0b00001,0b00001,0b11111],
  '6': [0b11111,0b10000,0b10000,0b11111,0b10001,0b10001,0b11111],
  '7': [0b11111,0b00001,0b00010,0b00100,0b00100,0b00100,0b00100],
  '8': [0b11111,0b10001,0b10001,0b11111,0b10001,0b10001,0b11111],
  '9': [0b11111,0b10001,0b10001,0b11111,0b00001,0b00001,0b11111],
  ':': [0b00000,0b00100,0b00100,0b00000,0b00100,0b00100,0b00000],
  '.': [0b00000,0b00000,0b00000,0b00000,0b00000,0b01100,0b01100],
  '/': [0b00001,0b00010,0b00010,0b00100,0b01000,0b01000,0b10000],
  ' ': [0b00000,0b00000,0b00000,0b00000,0b00000,0b00000,0b00000],
};

/**
 * Draw blocky pixel text using filled rectangles.
 * Each pixel in the 5x7 glyph becomes a solid block — ultra boxy retro look.
 * pixelSize = size of each block in screen pixels.
 */
function blockText(ctx, text, x, y, color, pixelSize, gap) {
  const charGap = gap !== undefined ? gap : 1;
  const charW = 5;
  const charH = 7;
  const outlineW = Math.max(1, Math.floor(pixelSize * 0.15));
  let cx = x;

  for (let c = 0; c < text.length; c++) {
    const glyph = BLOCK_FONT[text[c]];
    if (!glyph) { cx += (charW + charGap) * pixelSize; continue; }

    for (let row = 0; row < charH; row++) {
      for (let col = 0; col < charW; col++) {
        if (glyph[row] & (1 << (charW - 1 - col))) {
          const bx = cx + col * pixelSize;
          const by = y + row * pixelSize;
          // Black outline
          ctx.fillStyle = '#000';
          ctx.fillRect(bx - outlineW, by - outlineW, pixelSize + outlineW * 2, pixelSize + outlineW * 2);
        }
      }
    }
    for (let row = 0; row < charH; row++) {
      for (let col = 0; col < charW; col++) {
        if (glyph[row] & (1 << (charW - 1 - col))) {
          ctx.fillStyle = color;
          ctx.fillRect(cx + col * pixelSize, y + row * pixelSize, pixelSize, pixelSize);
        }
      }
    }

    cx += (charW + charGap) * pixelSize;
  }

  return cx - x; // return total width drawn
}

/**
 * Draw condensed pixel text with black outline (futuristic-retro style).
 * Uses scaleX to compress the font for a tall, narrow, techy feel.
 */
function pixelText(ctx, text, x, y, color, size, condense) {
  const squeeze = condense || 0.7;
  ctx.save();
  ctx.font = `bold ${size}px monospace`;
  ctx.translate(x, y);
  ctx.scale(squeeze, 1);
  // Black outline
  ctx.fillStyle = '#000';
  ctx.fillText(text, -2, 0);
  ctx.fillText(text, 2, 0);
  ctx.fillText(text, 0, -2);
  ctx.fillText(text, 0, 2);
  // Main color
  ctx.fillStyle = color;
  ctx.fillText(text, 0, 0);
  ctx.restore();
}

/**
 * Draw the HUD overlay - classic Amiga F1 style.
 * Labels top-left in red, time top-center, large minimap top-right.
 */
export function renderHUD(ctx, canvasW, canvasH, hudData) {
  const {
    speed, lap, totalLaps, currentLapTime, bestLapTime,
    lapTimes, commentary, minimapPoints, playerProgress, isOffroad
  } = hudData;

  const isBig = canvasW > 600;
  const pad = isBig ? 14 : 8;
  const PINK = '#ff00bd';

  ctx.save();
  ctx.textBaseline = 'top';

  // === Layout calculations ===
  const lapPx = isBig ? 4 : 2.5;
  const heroPx = isBig ? 14 : 7;
  const labelSize = isBig ? 16 : 10;
  const labelGap = isBig ? 30 : 16;
  const colGap = isBig ? 30 : 20;

  // Heights: hero and laps should match
  const heroH = 7 * heroPx;                     // 91px desktop
  const lapRowH = Math.floor(heroH / totalLaps) + (isBig ? 3 : 2); // extra gap between rows
  const lapCharW = 6 * lapPx;                    // width per char in lap times
  const heroCharW = 6 * heroPx;                  // width per char in hero nums

  // All labels share the same Y baseline
  const labelY = pad;
  const valueY = pad + labelGap;

  // === Column 1: LAPS ===
  const lapsX = pad;
  const lapsW = 8 * lapCharW;  // "00:00:00" = 8 chars

  pixelText(ctx, 'LAPS', lapsX, labelY, PINK, labelSize, 0.65);

  for (let i = 0; i < totalLaps; i++) {
    let timeStr = '00:00:00';
    let color = '#555555';
    if (lapTimes && lapTimes[i] !== undefined) {
      timeStr = formatTimeDetailed(lapTimes[i]);
      const isBest = bestLapTime < Infinity && lapTimes[i] <= bestLapTime;
      color = isBest ? '#ffcc00' : '#ffffff';
    } else if (i === lap - 1) {
      timeStr = formatTimeDetailed(currentLapTime);
      color = '#ffffff';
    }
    blockText(ctx, timeStr, lapsX, valueY + i * lapRowH, color, lapPx);
  }

  // === Column 2: TIME ===
  const timeX = lapsX + lapsW + colGap;

  pixelText(ctx, 'TIME', timeX, labelY, PINK, labelSize, 0.65);
  const secs = currentLapTime ? Math.floor(currentLapTime / 1000) : 0;
  const secsStr = secs < 10 ? '0' + secs : '' + secs;
  const timeW = blockText(ctx, secsStr, timeX, valueY, '#ffffff', heroPx);

  // === Column 3: SPEED ===
  const speedX = timeX + timeW + colGap;

  pixelText(ctx, 'SPEED', speedX, labelY, PINK, labelSize, 0.65);
  blockText(ctx, `${Math.floor(speed)}`, speedX, valueY, '#ffffff', heroPx);

  // === TOP RIGHT: Minimap — inset from corner to avoid close button ===
  if (minimapPoints && minimapPoints.length > 0) {
    const mapSize = isBig ? 150 : 85;
    const mapX = canvasW - mapSize - (isBig ? 80 : 50);
    const mapY = pad;

    // Track outline — white
    const step = Math.max(1, Math.floor(minimapPoints.length / 180));
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = isBig ? 3 : 2;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.beginPath();
    for (let i = 0; i < minimapPoints.length; i += step) {
      const px = mapX + minimapPoints[i].x * mapSize;
      const py = mapY + minimapPoints[i].y * mapSize;
      if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.stroke();

    // Player dot — pink
    const pidx = Math.floor(playerProgress * minimapPoints.length) % minimapPoints.length;
    if (minimapPoints[pidx]) {
      const dotX = mapX + minimapPoints[pidx].x * mapSize;
      const dotY = mapY + minimapPoints[pidx].y * mapSize;
      const dotR = isBig ? 6 : 4;

      ctx.fillStyle = PINK;
      ctx.beginPath();
      ctx.arc(dotX, dotY, dotR, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // === COMMENTARY (bottom center) ===
  if (commentary) {
    ctx.textAlign = 'center';
    pixelText(ctx, commentary, canvasW / 2, canvasH - (isBig ? 140 : 90), '#ffcc00', isBig ? 24 : 15, 0.65);
    ctx.textAlign = 'start';
  }

  // === OFF-ROAD warning ===
  if (isOffroad) {
    ctx.textAlign = 'center';
    pixelText(ctx, 'OFF TRACK!', canvasW / 2, canvasH - (isBig ? 55 : 38), '#ff4444', isBig ? 32 : 20, 0.6);
    ctx.textAlign = 'start';
  }

  ctx.restore();
}

/**
 * Format time as seconds with one decimal (classic style: "55.5").
 */
function formatTimeLarge(ms) {
  if (ms === Infinity || ms === undefined) return '0.0';
  const secs = ms / 1000;
  return secs.toFixed(1);
}

/**
 * Format time as MM:SS:CC (minutes:seconds:centiseconds) — "00:00:00" style.
 */
function formatTimeDetailed(ms) {
  if (ms === Infinity || ms === undefined || ms === 0) return '00:00:00';
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  const cs = Math.floor((ms % 1000) / 10);
  return mins.toString().padStart(2, '0') + ':' +
         secs.toString().padStart(2, '0') + ':' +
         cs.toString().padStart(2, '0');
}

/**
 * Draw the title screen.
 */
export function renderTitleScreen(ctx, canvasW, canvasH, track, scrollOffset) {
  renderScene(ctx, canvasW, canvasH, track, scrollOffset, 0, 100, 300, false);

  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.fillRect(0, 0, canvasW, canvasH);
}

/**
 * Draw the countdown overlay.
 */
export function renderCountdown(ctx, canvasW, canvasH, count) {
  ctx.save();

  const now = performance.now();
  const cx = canvasW / 2;
  const isGo = count === 0;

  // Detect count change for bounce animation
  if (count !== lastCountdownValue) {
    lastCountdownValue = count;
    countdownChangeTime = now;
  }

  const elapsed = now - countdownChangeTime;

  // Pick the right Mario Kart light sprite
  let sprite = null;
  if (LIGHT_SPRITES.loaded) {
    if (count >= 3) sprite = LIGHT_SPRITES[0];
    else if (count === 2) sprite = LIGHT_SPRITES[1];
    else if (count === 1) sprite = LIGHT_SPRITES[2];
    else if (count === 0) sprite = LIGHT_SPRITES.go;
  }

  // Screen flash on GO
  if (isGo && elapsed < 200) {
    const flashAlpha = (1 - elapsed / 200) * 0.5;
    ctx.fillStyle = `rgba(0, 255, 0, ${flashAlpha})`;
    ctx.fillRect(0, 0, canvasW, canvasH);
  }

  // Red flash on each red light
  if (!isGo && count < 3 && elapsed < 150) {
    const flashAlpha = (1 - elapsed / 150) * 0.25;
    ctx.fillStyle = `rgba(255, 0, 0, ${flashAlpha})`;
    ctx.fillRect(0, 0, canvasW, canvasH);
  }

  if (sprite) {
    ctx.imageSmoothingEnabled = false;

    const maxH = Math.min(canvasH * 0.45, 360);
    const aspect = sprite.width / sprite.height;

    // Bounce: scale up then settle back on each change
    let bounce = 1.0;
    if (elapsed < 300) {
      const t = elapsed / 300;
      // Elastic overshoot: jumps to 1.15 then springs back to 1.0
      bounce = 1.0 + 0.15 * Math.sin(t * Math.PI) * (1 - t);
    }

    const drawH = maxH * bounce;
    const drawW = drawH * aspect;
    const drawX = cx - drawW / 2;
    const drawY = canvasH * 0.12 - (drawH - maxH) / 2; // keep centered during bounce

    // Glow behind the lights
    const glowColor = isGo ? 'rgba(0, 220, 0,' : 'rgba(220, 0, 0,';
    if (count < 3) {
      // Pulsing glow
      const pulse = 0.3 + Math.sin(now * 0.006) * 0.1;
      const glowR = Math.max(drawW, drawH) * 0.8;
      const glow = ctx.createRadialGradient(cx, drawY + drawH / 2, 0, cx, drawY + drawH / 2, glowR);
      glow.addColorStop(0, glowColor + pulse + ')');
      glow.addColorStop(1, glowColor + '0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(cx, drawY + drawH / 2, glowR, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.drawImage(sprite, drawX, drawY, drawW, drawH);
  }

  // Countdown number below lights
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const numY = canvasH * 0.62;
  const numSize = Math.min(canvasW, canvasH) * 0.2;

  if (isGo) {
    // GO text with bounce
    let goScale = 1.0;
    if (elapsed < 400) {
      const t = elapsed / 400;
      goScale = 1.0 + 0.3 * Math.sin(t * Math.PI) * (1 - t * 0.5);
    }
    const goSize = Math.floor(numSize * goScale);
    pixelText(ctx, 'GO!', cx, numY, '#00dd00', goSize);
  } else if (count <= 3 && count >= 1) {
    // Number with bounce
    let numScale = 1.0;
    if (elapsed < 300) {
      const t = elapsed / 300;
      numScale = 1.0 + 0.2 * Math.sin(t * Math.PI) * (1 - t);
    }
    const size = Math.floor(numSize * numScale);
    pixelText(ctx, String(count), cx, numY, '#ffffff', size);
  }

  ctx.restore();
}

/**
 * Draw the results screen.
 */
export function renderResults(ctx, canvasW, canvasH, results) {
  const { totalTime, lapTimes, bestLapIndex } = results;
  const isBig = canvasW > 600;

  ctx.fillStyle = 'rgba(0,0,0,0.8)';
  ctx.fillRect(0, 0, canvasW, canvasH);

  const cx = canvasW / 2;
  ctx.textAlign = 'center';

  const checkerSize = isBig ? 16 : 10;
  for (let x = 0; x < canvasW; x += checkerSize) {
    for (let row = 0; row < 2; row++) {
      ctx.fillStyle = ((x / checkerSize + row) % 2 === 0) ? '#fff' : '#000';
      ctx.fillRect(x, row * checkerSize, checkerSize, checkerSize);
    }
  }

  const titleSize = isBig ? 36 : 22;
  ctx.font = `bold ${titleSize}px monospace`;
  ctx.fillStyle = '#ffcc00';
  ctx.fillText('RACE COMPLETE!', cx, canvasH * 0.18);

  const subSize = isBig ? 22 : 14;
  ctx.font = `bold ${subSize}px monospace`;
  ctx.fillStyle = '#fff';
  ctx.fillText(`TOTAL: ${formatTime(totalTime)}`, cx, canvasH * 0.28);

  const lapSize = isBig ? 18 : 12;
  ctx.font = `${lapSize}px monospace`;
  for (let i = 0; i < lapTimes.length; i++) {
    const best = i === bestLapIndex;
    ctx.fillStyle = best ? '#ffcc00' : '#ccc';
    ctx.fillText(
      `${best ? '* ' : '  '}LAP ${i + 1}: ${formatTime(lapTimes[i])}${best ? ' (BEST)' : ''}`,
      cx, canvasH * 0.36 + i * (lapSize + 8)
    );
  }

  ctx.textAlign = 'start';
}

function formatTime(ms) {
  if (ms === Infinity || ms === undefined) return '--:--.---';
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  const millis = Math.floor(ms % 1000);
  return `${mins}:${secs.toString().padStart(2, '0')}.${millis.toString().padStart(3, '0')}`;
}
